/**
 * Todo API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  BulkAssignRequest,
  BulkCompleteRequest,
  BulkDeleteRequest,
  ConvertToTaskRequest,
  CreateTodoRequest,
  QuickCreateTodoRequest,
  Todo,
  TodoCategory,
  TodoListParams,
  TodoListResponse,
  TodoStats,
  UpdateTodoRequest,
} from '../types/todo';

/**
 * Todo API Endpoints
 */
export const todoApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // LIST & GET
    // ============================================================================

    // List todos for a project
    getTodos: builder.query<
      ApiResponse<TodoListResponse>,
      { projectId: string; params?: TodoListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/todos`,
        params,
      }),
      providesTags: ['Todo'],
    }),

    // Get single todo
    getTodo: builder.query<ApiResponse<Todo>, { projectId: string; todoId: string }>({
      query: ({ projectId, todoId }) => ({
        url: `/projects/${projectId}/todos/${todoId}`,
      }),
      providesTags: (_result, _error, { todoId }) => [{ type: 'Todo', id: todoId }],
    }),

    // Get stats for todos in a project
    getTodoStats: builder.query<ApiResponse<TodoStats>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/todos/stats`,
      }),
      providesTags: ['Todo'],
    }),

    // ============================================================================
    // CATEGORIES
    // ============================================================================

    // List categories
    getTodoCategories: builder.query<ApiResponse<TodoCategory[]>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/todos/categories`,
      }),
      providesTags: ['Todo'],
    }),

    // Create category
    createTodoCategory: builder.mutation<
      ApiResponse<TodoCategory>,
      { projectId: string; data: { name: string } }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/todos/categories`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Todo'],
    }),

    // Delete category
    deleteTodoCategory: builder.mutation<
      ApiResponse<null>,
      { projectId: string; categoryId: string }
    >({
      query: ({ projectId, categoryId }) => ({
        url: `/projects/${projectId}/todos/categories/${categoryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Todo'],
    }),

    // ============================================================================
    // CRUD
    // ============================================================================

    // Create todo
    createTodo: builder.mutation<ApiResponse<Todo>, { projectId: string; data: CreateTodoRequest }>(
      {
        query: ({ projectId, data }) => ({
          url: `/projects/${projectId}/todos`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: ['Todo'],
      }
    ),

    // Quick-create todo
    quickCreateTodo: builder.mutation<
      ApiResponse<Todo>,
      { projectId: string; data: QuickCreateTodoRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/todos/quick`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Todo'],
    }),

    // Update todo
    updateTodo: builder.mutation<
      ApiResponse<Todo>,
      { projectId: string; todoId: string; data: UpdateTodoRequest }
    >({
      query: ({ projectId, todoId, data }) => ({
        url: `/projects/${projectId}/todos/${todoId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { todoId }) => ['Todo', { type: 'Todo', id: todoId }],
    }),

    // Delete todo
    deleteTodo: builder.mutation<ApiResponse<null>, { projectId: string; todoId: string }>({
      query: ({ projectId, todoId }) => ({
        url: `/projects/${projectId}/todos/${todoId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Todo'],
    }),

    // ============================================================================
    // STATUS ACTIONS
    // ============================================================================

    // Complete todo
    completeTodo: builder.mutation<ApiResponse<Todo>, { projectId: string; todoId: string }>({
      query: ({ projectId, todoId }) => ({
        url: `/projects/${projectId}/todos/${todoId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { todoId }) => ['Todo', { type: 'Todo', id: todoId }],
    }),

    // Reopen todo
    reopenTodo: builder.mutation<ApiResponse<Todo>, { projectId: string; todoId: string }>({
      query: ({ projectId, todoId }) => ({
        url: `/projects/${projectId}/todos/${todoId}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { todoId }) => ['Todo', { type: 'Todo', id: todoId }],
    }),

    // ============================================================================
    // BULK OPERATIONS
    // ============================================================================

    // Bulk complete
    bulkCompleteTodos: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; data: BulkCompleteRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/todos/bulk/complete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Todo'],
    }),

    // Bulk assign
    bulkAssignTodos: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; data: BulkAssignRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/todos/bulk/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Todo'],
    }),

    // Bulk delete
    bulkDeleteTodos: builder.mutation<
      ApiResponse<{ deletedCount: number }>,
      { projectId: string; data: BulkDeleteRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/todos/bulk/delete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Todo'],
    }),

    // ============================================================================
    // CONVERT TO TASK
    // ============================================================================

    // Convert todo to task
    convertTodoToTask: builder.mutation<
      ApiResponse<{ todo: Todo; taskId: string }>,
      { projectId: string; todoId: string; data: ConvertToTaskRequest }
    >({
      query: ({ projectId, todoId, data }) => ({
        url: `/projects/${projectId}/todos/${todoId}/convert-to-task`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Todo', 'Task'],
    }),
  }),
});

// Export all hooks
export const {
  useGetTodosQuery,
  useGetTodoQuery,
  useGetTodoStatsQuery,
  useGetTodoCategoriesQuery,
  useCreateTodoCategoryMutation,
  useDeleteTodoCategoryMutation,
  useCreateTodoMutation,
  useQuickCreateTodoMutation,
  useUpdateTodoMutation,
  useDeleteTodoMutation,
  useCompleteTodoMutation,
  useReopenTodoMutation,
  useBulkCompleteTodosMutation,
  useBulkAssignTodosMutation,
  useBulkDeleteTodosMutation,
  useConvertTodoToTaskMutation,
} = todoApi;
